import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getPublicApartmentCheckin,
  getPublicCheckinForm,
  startPublicCheckin,
  findGuideByIdentifier,
  type PublicApartmentCheckin,
  type PublicCheckinForm as PublicCheckinFormData,
} from '../api.publicCheckin';
import customerServiceIcon from '../assets/customer-service.png';
import { CHECKIN_STORAGE_KEY, saveCheckinEntry, readCheckinEntry } from '../utils/checkinStorage';
import useIsMobile from '../hooks/useIsMobile';

const LANGUAGE_STORAGE_KEY = 'guest-form-language';
type GuestLanguage = 'cs' | 'en';

type StartText = {
  loading: string;
  title: string;
  czech: string;
  english: string;
  checkInDate: string;
  numberOfGuests: string;
  numberOfGuestsHint: string;
  email: string;
  emailHint: string;
  eta: string;
  etaPlaceholder: string;
  continueBtn: string;
  requiredMark: string;
  requiredField: string;
  invalidGuests: string;
  maxCapacity: string;
  dateBeforeToday: string;
  dateAfterWindow: string;
  startFailed: string;
  complianceTitle: string;
  shareTitle: string;
  shareInstructions: string;
  shareCopy: string;
  shareCopied: string;
  shareNativeBtn: string;
  shareContinue: string;
  lockedTitle: string;
  lockedBy: string;
  lockedAdvice: string;
  contactAssistance: string;
  stepsIntro: string;
  step1Eyebrow: string;
  step1Title: string;
  step1Sub: string;
  alreadyRegisteredLabel: string;
  step2Eyebrow: string;
  step2Title: string;
  step2Sub: string;
  lookupLabel: string;
  lookupPlaceholder: string;
  lookupButton: string;
  lookupChecking: string;
  notFoundTitle: string;
  notFoundMsg: string;
  tryAgainBtn: string;
  incompleteTitle: string;
  incompleteMsg: (completed: number, total: number) => string;
  incompleteBtn: string;
};

const TEXT: Record<GuestLanguage, StartText> = {
  cs: {
    loading: 'Načítám...',
    title: 'Online check-in',
    czech: 'Čeština',
    english: 'English',
    checkInDate: 'Datum příjezdu',
    numberOfGuests: 'Počet hostů',
    numberOfGuestsHint: 'Celkový počet všech členů skupiny včetně vás.',
    email: 'E-mail',
    emailHint: 'Na tento e-mail vám pošleme odkaz na registrační formulář.',
    eta: 'Předpokládaný čas příjezdu (volitelné)',
    etaPlaceholder: 'např. 18:30',
    continueBtn: 'Pokračovat na formulář hostů',
    requiredMark: '*',
    requiredField: 'Toto pole je povinné.',
    invalidGuests: 'Zadejte platný počet hostů.',
    maxCapacity: 'Maximum je {capacity} hostů.',
    dateBeforeToday: 'Datum příjezdu nemůže být v minulosti.',
    dateAfterWindow: 'Toto datum je mimo povolené okno příjezdu. Registrační formulář pro toto datum nemusí být připraven.',
    startFailed: 'Check-in nelze spustit. Zkontrolujte prosím zadané údaje.',
    complianceTitle: 'Právní informace',
    shareTitle: 'Sdílejte tento odkaz se všemi členy skupiny',
    shareInstructions: 'Tento odkaz je jediný způsob, jak mohou ostatní členové skupiny vyplnit své údaje. Pošlete jim ho před tím, než budete pokračovat.',
    shareCopy: 'Kopírovat odkaz',
    shareCopied: 'Zkopírováno!',
    shareNativeBtn: 'Sdílet přes telefon',
    shareContinue: 'Sdílel/a jsem odkaz — pokračovat',
    lockedTitle: 'Registrace již probíhá',
    lockedBy: 'Tuto rezervaci již zaregistroval/a',
    lockedAdvice: 'Pokud je tento e-mail váš, opravte ho níže a odešlete formulář znovu. Pokud jste si jistí, že e-mail je správně, zkontrolujte, že jste zvolili správné datum příjezdu, nebo kontaktujte ubytovatele.',
    contactAssistance: 'Potřebujete pomoc?',
    stepsIntro: 'Dva kroky pro váš pobyt:',
    step1Eyebrow: 'Krok 1 · Povinné',
    step1Title: 'Zaregistrujte se a odešlete Knihu hostů',
    step1Sub: 'Každý host vyplní své údaje — zabere to asi 2 minuty.',
    alreadyRegisteredLabel: 'Už jste se zaregistrovali?',
    step2Eyebrow: 'Krok 2 · Po registraci',
    step2Title: 'Průvodce ubytováním',
    step2Sub: 'Klíče, WiFi, parkování a další — dostupné po odeslání Kroku 1.',
    lookupLabel: 'E-mail nebo telefon použitý při registraci',
    lookupPlaceholder: 'vy@example.com nebo +420 777 123 456',
    lookupButton: 'Najít',
    lookupChecking: 'Hledám...',
    notFoundTitle: '😕 Nepodařilo se vás najít',
    notFoundMsg: 'Žádná registrace pro tento apartmán neodpovídá zadanému e-mailu ani telefonu. Zkontrolujte zadané údaje, nebo pokud jste se ještě neregistrovali, vyplňte Krok 1 výše.',
    tryAgainBtn: 'Zkusit znovu',
    incompleteTitle: '👋 Našli jsme vás!',
    incompleteMsg: (completed, total) => `Vaše registrace ještě není dokončená, takže Průvodce zatím není dostupný — zbývá vyplnit údaje ještě pro ${total - completed} z ${total} hostů.`,
    incompleteBtn: 'Dokončit formulář hostů →',
  },
  en: {
    loading: 'Loading...',
    title: 'Online Check-in',
    czech: 'Čeština',
    english: 'English',
    checkInDate: 'Check-in Date',
    numberOfGuests: 'Number of Guests',
    numberOfGuestsHint: 'Total number of all members in your group, including yourself.',
    email: 'Email',
    emailHint: 'The guest registration form link will be sent to this email address.',
    eta: 'Estimated Arrival Time (optional)',
    etaPlaceholder: 'e.g. 18:30',
    continueBtn: 'Continue to Guest Form',
    requiredMark: '*',
    requiredField: 'This field is required.',
    invalidGuests: 'Please enter a valid number of guests.',
    maxCapacity: 'Maximum is {capacity} guests.',
    dateBeforeToday: 'Check-in date cannot be in the past.',
    dateAfterWindow: 'This date is outside the allowed check-in window. A registration form for this date may not be prepared yet.',
    startFailed: 'Unable to start check-in. Please verify your details.',
    complianceTitle: 'Legal Notice',
    shareTitle: 'Share this link with everyone in your group',
    shareInstructions: 'This is the only link your travel companions can use to fill in their details. Send it to them before you continue.',
    shareCopy: 'Copy link',
    shareCopied: 'Copied!',
    shareNativeBtn: 'Share via phone',
    shareContinue: "I've shared it — continue",
    lockedTitle: 'Registration already in progress',
    lockedBy: 'This reservation was already registered by',
    lockedAdvice: 'If this email is yours, correct it below and submit again to continue. If you’re sure the email is right, double-check you selected the correct check-in date, or contact your host.',
    contactAssistance: 'Need assistance?',
    stepsIntro: 'Two steps for your stay:',
    step1Eyebrow: 'Step 1 · Required',
    step1Title: 'Register & Submit Guestbook',
    step1Sub: 'Every guest fills in their details — about 2 minutes each.',
    alreadyRegisteredLabel: 'Already registered?',
    step2Eyebrow: 'Step 2 · After registration',
    step2Title: 'Check-in Guide',
    step2Sub: 'Keys, WiFi, parking & more — available once Step 1 is submitted.',
    lookupLabel: 'Email or phone used when you registered',
    lookupPlaceholder: 'you@example.com or +420 777 123 456',
    lookupButton: 'Find',
    lookupChecking: 'Looking...',
    notFoundTitle: "😕 We couldn't find you",
    notFoundMsg: "No registration matches that email or phone for this apartment. Double-check what you entered, or complete Step 1 above if you haven't yet.",
    tryAgainBtn: 'Try again',
    incompleteTitle: '👋 We found you!',
    incompleteMsg: (completed, total) => `Your registration isn't complete yet, so your Guide isn't available — ${total - completed} of ${total} guests still need to fill in their details.`,
    incompleteBtn: 'Complete the Guest Form →',
  },
};

const formatText = (template: string, values: Record<string, string | number>) =>
  template.replace(/\{(\w+)}/g, (_, key) => String(values[key] ?? ''));

/**
 * Parses a "YYYY-MM-DD" string as a LOCAL midnight Date, not UTC midnight.
 * `new Date("YYYY-MM-DD")` is parsed as UTC per the JS spec, which silently shifts
 * the date by a day when compared against local "today" in negative-UTC-offset
 * timezones (e.g. French Guiana, Martinique, French Polynesia) — a guest picking
 * today's date would otherwise get rejected as "in the past".
 */
const parseLocalDate = (isoDate: string): Date => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

/** Formats a Date as "YYYY-MM-DD" using LOCAL date parts (toISOString() would shift to UTC). */
const toLocalIsoDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Guide if it's accessible (complete, or manager force-opened it, and not manager-blocked); otherwise the Guest Form. */
const destinationFor = (token: string, formData: PublicCheckinFormData | null): string => {
  if (!formData?.guideEnabled) return `/check-in/form/${token}`;
  const mode = formData.guideAccessMode ?? 'AUTO';
  if (mode === 'BLOCKED') return `/check-in/form/${token}`;
  const count = Math.max(1, formData.numberOfGuests || 1);
  const completed = (formData.members ?? []).filter(m => m.isComplete).length;
  const allDone = completed >= count;
  return (allDone || mode === 'OPEN') ? `/guide/${token}` : `/check-in/form/${token}`;
};

const getPublicCheckinErrorMessage = (err: any, fallback: string) => {
  if (err?.response?.status === 429) {
    return err.response?.data?.error || 'Too many attempts. Please wait a moment and try again.';
  }
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
};

const getContactInfo = (meta: PublicApartmentCheckin | null): { name?: string; email?: string; phone?: string } => {
  if (!meta) return {};
  const contactType = meta.guestFooterContactType || 'manager';
  if (contactType === 'host') {
    if (meta.hostFullName || meta.hostPhone) return { name: meta.hostFullName, phone: meta.hostPhone };
    return { name: meta.managerFullName, email: meta.managerEmail, phone: meta.managerPhone };
  }
  if (contactType === 'contact') {
    return { name: meta.contactFullName, email: meta.contactEmail, phone: meta.contactPhone };
  }
  return { name: meta.managerFullName, email: meta.managerEmail, phone: meta.managerPhone };
};

const BrandedHeader: React.FC<{
  title: string;
  meta: PublicApartmentCheckin | null;
  language: GuestLanguage;
  setLanguage: (l: GuestLanguage) => void;
  t: StartText;
}> = ({ title, meta, language, setLanguage, t }) => {
  const isMobile = useIsMobile();
  return (
  <div style={{
    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
    borderRadius: '0 0 24px 24px',
    padding: '20px 16px 24px',
    marginBottom: -16,
    color: '#fff',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: 'rgba(255,255,255,0.2)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
      </div>
      <span style={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.01em' }}>
        Guestbook<span style={{ color: '#93c5fd' }}>.cz</span>
      </span>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ margin: '0 0 6px', fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</h2>
        {meta && (
          <div style={{ fontSize: '0.9rem', opacity: 0.85, lineHeight: 1.6 }}>
            <div><strong>{meta.apartmentName}</strong></div>
            {meta.apartmentAddress && <div>{meta.apartmentAddress}</div>}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {(['cs', 'en'] as GuestLanguage[]).map(lang => (
          <button key={lang} type="button" onClick={() => setLanguage(lang)} style={{
            padding: isMobile ? '9px 14px' : '5px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 700,
            fontSize: isMobile ? '0.86rem' : '0.82rem',
            border: language === lang ? '1.5px solid rgba(255,255,255,0.8)' : '1.5px solid rgba(255,255,255,0.3)',
            background: language === lang ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
            color: '#fff',
            backdropFilter: 'blur(4px)',
          }}>
            {lang === 'cs' ? '🇨🇿 ' + t.czech : '🇬🇧 ' + t.english}
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

type StepTheme = 'blue' | 'gold';

const STEP_THEME = {
  blue: {
    accent: '#2563eb',
    border: '#bfdbfe',
    wash: 'linear-gradient(155deg, #eff6ff 0%, #fff 55%)',
    numBg: 'linear-gradient(135deg, #2563eb, #1e3a8a)',
    numShadow: '0 3px 8px rgba(37,99,235,0.35)',
    eyebrow: '#2563eb',
  },
  gold: {
    accent: '#f59e0b',
    border: '#fcd34d',
    wash: 'linear-gradient(155deg, #fffbeb 0%, #fff 55%)',
    numBg: 'linear-gradient(135deg, #f59e0b, #b45309)',
    numShadow: '0 3px 8px rgba(180,83,9,0.35)',
    eyebrow: '#b45309',
  },
} as const;

const StepCard: React.FC<{
  num: number;
  theme: StepTheme;
  eyebrow: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ num, theme, eyebrow, title, subtitle, open, onToggle, children }) => {
  const colors = STEP_THEME[theme];
  const isMobile = useIsMobile();
  return (
    <div style={{
      borderRadius: 16,
      border: `1.5px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.accent}`,
      background: colors.wash,
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: isMobile ? '16px 14px' : 14, cursor: 'pointer', background: 'none', border: 'none',
          width: '100%', textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '0.86rem', color: '#fff',
          background: colors.numBg, boxShadow: colors.numShadow,
        }}>
          {num}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 2px', color: colors.eyebrow }}>{eyebrow}</p>
          <p style={{ fontWeight: 800, fontSize: '0.98rem', margin: '0 0 2px', color: '#0f172a', letterSpacing: '-0.01em' }}>{title}</p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0, lineHeight: 1.4 }}>{subtitle}</p>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.18s ease' }}
        >
          <path d="m9 6 6 6-6 6"/>
        </svg>
      </button>
      {open && (
        <div style={{ padding: '0 14px 16px', borderTop: '1px solid rgba(15,23,42,0.06)', marginTop: 2, paddingTop: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
};

type Step2View =
  | { kind: 'lookup' }
  | { kind: 'checking' }
  | { kind: 'notfound' }
  | { kind: 'incomplete'; formToken: string; completed: number; total: number };

const PublicCheckinStart: React.FC = () => {
  const { publicKey } = useParams<{ publicKey: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<PublicApartmentCheckin | null>(null);
  const [lockedByMaskedEmail, setLockedByMaskedEmail] = useState<string | null>(null);
  const [language, setLanguage] = useState<GuestLanguage>(() => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'cs' || stored === 'en') return stored;
    return window.navigator.language?.toLowerCase().startsWith('cs') ? 'cs' : 'en';
  });

  const [form, setForm] = useState({ checkInDate: '', email: '', estimatedArrivalTime: '' });
  const [guestCountRaw, setGuestCountRaw] = useState('');
  const [dateFeedback, setDateFeedback] = useState<{ msg: string; warn: boolean } | null>(null);

  // Only one step is expanded at a time; both start collapsed.
  const [activeStep, setActiveStep] = useState<1 | 2 | null>(null);
  const [step1Checking, setStep1Checking] = useState(false);
  const [step2View, setStep2View] = useState<Step2View>({ kind: 'lookup' });
  const [lookupValue, setLookupValue] = useState('');

  const t = TEXT[language];
  const maxCapacity = meta?.apartmentCapacity;

  useEffect(() => {
    const run = async () => {
      if (!publicKey) return;
      setLoading(true);
      setError(null);

      // Housekeeping only — no auto-redirect. The guest always sees both steps and
      // only moves on when they press one. readCheckinEntry() clears the entry if it's
      // past the reservation's checkout date or the 90-day safety cap.
      readCheckinEntry(publicKey);

      try {
        const apartmentMeta = await getPublicApartmentCheckin(publicKey);
        setMeta(apartmentMeta);
        // Fire-and-forget: warm up the calendar sync so reservation is ready when guest submits
        fetch(`/api/public/checkin/${publicKey}/trigger-sync`, { method: 'POST' }).catch(() => {});
      } catch (err: any) {
        setError(getPublicCheckinErrorMessage(err, t.startFailed));
      }
      setLoading(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey]);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'numberOfGuests') { setGuestCountRaw(value); return; }
    setForm((f) => ({ ...f, [name]: value }));
    if (name === 'checkInDate') {
      if (!value) { setDateFeedback(null); return; }
      const sel = parseLocalDate(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (sel < today) {
        setDateFeedback({ msg: t.dateBeforeToday, warn: false });
      } else if (meta?.windowEnd && sel > parseLocalDate(meta.windowEnd)) {
        setDateFeedback({ msg: t.dateAfterWindow, warn: true });
      } else {
        setDateFeedback(null);
      }
    }
  };

  const normalizeGuestCount = (raw: string): number | null => {
    if (!raw.trim()) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    const intValue = Math.floor(n);
    if (intValue < 1) return null;
    if (maxCapacity && maxCapacity > 0 && intValue > maxCapacity) return null;
    return intValue;
  };

  const onGuestCountBlur = () => {
    if (!guestCountRaw.trim()) return;
    const normalized = normalizeGuestCount(guestCountRaw);
    if (normalized !== null) setGuestCountRaw(String(normalized));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publicKey) return;
    setError(null);

    if (!form.checkInDate) { setError(`${t.checkInDate}: ${t.requiredField}`); return; }
    const selectedDate = parseLocalDate(form.checkInDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) { setError(t.dateBeforeToday); return; }
    if (meta?.windowEnd) {
      const windowEnd = parseLocalDate(meta.windowEnd);
      if (selectedDate > windowEnd) { setError(t.dateAfterWindow); return; }
    }
    if (!form.email.trim()) { setError(`${t.email}: ${t.requiredField}`); return; }

    const guestCount = normalizeGuestCount(guestCountRaw);
    if (guestCount === null) {
      setError(maxCapacity && maxCapacity > 0
        ? formatText(t.maxCapacity, { capacity: maxCapacity })
        : t.invalidGuests);
      return;
    }

    try {
      const res = await startPublicCheckin(publicKey, {
        checkInDate: form.checkInDate,
        numberOfGuests: guestCount,
        email: form.email.trim(),
        estimatedArrivalTime: form.estimatedArrivalTime.trim(),
      });
      if (!res.success || !res.formToken) {
        setLockedByMaskedEmail(res.lockedByMaskedEmail ?? null);
        setError(res.lockedByMaskedEmail ? null : t.startFailed);
        return;
      }
      setLockedByMaskedEmail(null);
      localStorage.setItem(`guestbook-form-pk-${res.formToken}`, publicKey!);
      try {
        const formData = await getPublicCheckinForm(res.formToken);
        saveCheckinEntry(publicKey, {
          formToken: res.formToken,
          checkInDate: formData.checkInDate,
          checkOutDate: formData.checkOutDate,
          numberOfGuests: formData.numberOfGuests,
        });
        navigate(destinationFor(res.formToken, formData));
      } catch {
        navigate(`/check-in/form/${res.formToken}`);
      }
    } catch (err: any) {
      const lockedEmail = err?.response?.data?.lockedByMaskedEmail;
      if (lockedEmail) { setLockedByMaskedEmail(lockedEmail); setError(null); }
      else { setError(getPublicCheckinErrorMessage(err, t.startFailed)); }
    }
  };

  /**
   * Resolves what a known formToken means for Step 2: redirects straight to the Guide when it's
   * accessible, otherwise reports the remaining-guests count so the caller can show the
   * "found you but incomplete" message. Persists the token to localStorage either way so a
   * repeat visit resolves instantly next time.
   */
  const describeGuideOutcome = async (formToken: string): Promise<'redirected' | { completed: number; total: number }> => {
    const formData = await getPublicCheckinForm(formToken);
    if (publicKey) {
      saveCheckinEntry(publicKey, {
        formToken,
        checkInDate: formData.checkInDate,
        checkOutDate: formData.checkOutDate,
        numberOfGuests: formData.numberOfGuests,
      });
    }
    const dest = destinationFor(formToken, formData);
    if (dest.startsWith('/guide/')) {
      navigate(dest);
      return 'redirected';
    }
    const total = Math.max(1, formData.numberOfGuests || 1);
    const completed = (formData.members ?? []).filter(m => m.isComplete).length;
    return { completed, total };
  };

  const handleStep1Toggle = async () => {
    if (activeStep === 1) { setActiveStep(null); return; }
    if (step1Checking) return;
    const entry = publicKey ? readCheckinEntry(publicKey) : null;
    if (entry) {
      // Confirm the cached token still resolves before trusting it — a manager resetting this
      // reservation in the meantime kills the token server-side without touching this browser's
      // cache, and jumping straight there used to just bounce the guest off a dead link.
      setStep1Checking(true);
      try {
        await getPublicCheckinForm(entry.formToken);
        navigate(`/check-in/form/${entry.formToken}`);
        return;
      } catch {
        if (publicKey) localStorage.removeItem(CHECKIN_STORAGE_KEY(publicKey));
      } finally {
        setStep1Checking(false);
      }
    }
    setActiveStep(1);
  };

  const handleStep2Toggle = async () => {
    if (activeStep === 2) { setActiveStep(null); return; }
    setActiveStep(2);

    const entry = publicKey ? readCheckinEntry(publicKey) : null;
    if (!entry) { setStep2View({ kind: 'lookup' }); return; }

    setStep2View({ kind: 'checking' });
    try {
      const outcome = await describeGuideOutcome(entry.formToken);
      if (outcome !== 'redirected') {
        setStep2View({ kind: 'incomplete', formToken: entry.formToken, completed: outcome.completed, total: outcome.total });
      }
    } catch {
      if (publicKey) localStorage.removeItem(CHECKIN_STORAGE_KEY(publicKey));
      setStep2View({ kind: 'lookup' });
    }
  };

  const handleLookupSubmit = async () => {
    if (!publicKey || !lookupValue.trim()) return;
    setStep2View({ kind: 'checking' });
    try {
      const result = await findGuideByIdentifier(publicKey, lookupValue.trim());
      if (!result.found || !result.formToken) {
        setStep2View({ kind: 'notfound' });
        return;
      }
      const outcome = await describeGuideOutcome(result.formToken);
      if (outcome !== 'redirected') {
        setStep2View({ kind: 'incomplete', formToken: result.formToken, completed: outcome.completed, total: outcome.total });
      }
    } catch {
      setStep2View({ kind: 'notfound' });
    }
  };

  const handleLookupRetry = () => {
    setLookupValue('');
    setStep2View({ kind: 'lookup' });
  };

  const complianceText = useMemo(() => {
    if (!meta) return '';
    if (language === 'cs') return meta.complianceNoticeCZ || meta.complianceNoticeEN || '';
    return meta.complianceNoticeEN || meta.complianceNoticeCZ || '';
  }, [language, meta]);

  if (loading) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 16px', textAlign: 'center', color: '#4a5568' }}>
        {t.loading}
      </div>
    );
  }

  const contact = getContactInfo(meta);
  const showContactFooter = !!meta?.guestFooterContactType && (!!contact.name || !!contact.email || !!contact.phone);

  const contactFooter = showContactFooter ? (
    <div style={{
      marginTop: 16,
      padding: '14px 16px',
      background: '#eff6ff',
      borderRadius: 12,
      border: '1px solid #bfdbfe',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>{t.contactAssistance}</span>
        <img src={customerServiceIcon} alt="" width={56} height={56} style={{ flexShrink: 0, objectFit: 'contain' }} />
      </div>
      <div style={{ fontSize: '0.9rem', color: '#1e3a8a', lineHeight: 1.7 }}>
        {contact.name && <div><strong>{contact.name}</strong></div>}
        {contact.email && <div>📧 {contact.email}</div>}
        {contact.phone && <div>📞 {contact.phone}</div>}
      </div>
    </div>
  ) : null;

  const resultCardStyle: React.CSSProperties = {
    marginTop: 10, borderRadius: 10, padding: '10px 12px', fontSize: '0.8rem', lineHeight: 1.5,
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', paddingBottom: 40, overflowX: 'hidden' }}>
      <BrandedHeader title={t.title} meta={meta} language={language} setLanguage={setLanguage} t={t} />

      <div style={{ padding: '20px 16px 0' }}>
        <p style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center', margin: '0 0 14px' }}>{t.stepsIntro}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <StepCard
            num={1}
            theme="blue"
            eyebrow={t.step1Eyebrow}
            title={t.step1Title}
            subtitle={t.step1Sub}
            open={activeStep === 1}
            onToggle={handleStep1Toggle}
          >
            {complianceText && (
              <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginBottom: 16, color: '#2d3748' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.complianceTitle}</div>
                <div style={{ fontSize: '0.92rem' }}>{complianceText}</div>
              </div>
            )}

            {lockedByMaskedEmail && (
              <div style={{ background: '#fffbeb', border: '1px solid #f6ad55', borderRadius: 6, padding: '12px 14px', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: '#744210', marginBottom: 4 }}>🔒 {t.lockedTitle}</div>
                <div style={{ color: '#4a5568', marginBottom: 4 }}>{t.lockedBy} <strong>{lockedByMaskedEmail}</strong>.</div>
                <div style={{ color: '#4a5568', fontSize: '0.92rem' }}>{t.lockedAdvice}</div>
              </div>
            )}
            {error && <div style={{ background: '#fed7d7', color: '#742a2a', padding: 10, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

            <form onSubmit={onSubmit}>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">{t.checkInDate} {t.requiredMark}</label>
                  <input
                    className="form-control"
                    type="date"
                    name="checkInDate"
                    value={form.checkInDate}
                    onChange={onChange}
                    required
                    min={meta?.windowStart ?? toLocalIsoDate(new Date())}
                    max={meta?.windowEnd}
                    style={dateFeedback ? { borderColor: dateFeedback.warn ? '#f59e0b' : '#fc8181' } : undefined}
                  />
                  {dateFeedback && (
                    <div style={{
                      marginTop: 5,
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: '0.82rem',
                      fontWeight: 500,
                      background: dateFeedback.warn ? '#fffbeb' : '#fff5f5',
                      border: `1px solid ${dateFeedback.warn ? '#fcd34d' : '#feb2b2'}`,
                      color: dateFeedback.warn ? '#92400e' : '#c53030',
                    }}>
                      {dateFeedback.warn ? '⚠ ' : '✕ '}{dateFeedback.msg}
                    </div>
                  )}
                </div>
                <div className="form-field">
                  <label className="form-label">{t.numberOfGuests} {t.requiredMark}</label>
                  <input
                    className="form-control"
                    type="number"
                    min={1}
                    max={maxCapacity && maxCapacity > 0 ? maxCapacity : undefined}
                    name="numberOfGuests"
                    value={guestCountRaw}
                    onChange={onChange}
                    onBlur={onGuestCountBlur}
                    placeholder="1"
                    required
                  />
                  <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 4 }}>{t.numberOfGuestsHint}</div>
                </div>
                <div className="form-field">
                  <label className="form-label">{t.email} {t.requiredMark}</label>
                  <input className="form-control" type="email" name="email" value={form.email} onChange={onChange} required />
                  <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 4 }}>{t.emailHint}</div>
                </div>
                <div className="form-field">
                  <label className="form-label">{t.eta}</label>
                  <input
                    className="form-control"
                    type="text"
                    name="estimatedArrivalTime"
                    value={form.estimatedArrivalTime}
                    onChange={onChange}
                    placeholder={t.etaPlaceholder}
                  />
                </div>
              </div>

              <div className="form-actions" style={{ marginTop: 24 }}>
                <button
                  type="submit"
                  className={isMobile ? 'btn btn-primary' : 'btn btn-sm btn-primary'}
                  style={isMobile ? { padding: '13px 18px', width: '100%', fontSize: '0.95rem' } : { padding: '8px 18px' }}
                >
                  {t.continueBtn}
                </button>
              </div>
            </form>
          </StepCard>

          <div style={{ width: 2, height: 16, background: '#e2e8f0', margin: '0 0 0 33px' }} />

          <p style={{
            fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8',
            margin: '0 0 6px 33px', textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            {t.alreadyRegisteredLabel}
          </p>

          <StepCard
            num={2}
            theme="gold"
            eyebrow={t.step2Eyebrow}
            title={t.step2Title}
            subtitle={t.step2Sub}
            open={activeStep === 2}
            onToggle={handleStep2Toggle}
          >
            {step2View.kind === 'checking' && (
              <div style={{ fontSize: '0.85rem', color: '#64748b', padding: '4px 0' }}>{t.lookupChecking}</div>
            )}

            {step2View.kind === 'lookup' && (
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {t.lookupLabel}
                </label>
                <div style={{ display: 'flex', gap: 6, flexDirection: isMobile ? 'column' : 'row' }}>
                  <input
                    type="text"
                    value={lookupValue}
                    onChange={(e) => setLookupValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookupSubmit(); } }}
                    placeholder={t.lookupPlaceholder}
                    style={{ flex: 1, padding: isMobile ? '11px 12px' : '9px 11px', borderRadius: 9, border: '1.5px solid #cbd5e0', fontSize: '16px' }}
                  />
                  <button
                    type="button"
                    onClick={handleLookupSubmit}
                    style={{
                      padding: isMobile ? '11px 16px' : '0 16px', borderRadius: 9, border: 'none',
                      background: '#b45309', color: '#fff', fontWeight: 700, fontSize: '0.84rem',
                      cursor: 'pointer', whiteSpace: 'nowrap', width: isMobile ? '100%' : undefined,
                    }}
                  >
                    {t.lookupButton}
                  </button>
                </div>
              </div>
            )}

            {step2View.kind === 'notfound' && (
              <div style={{ ...resultCardStyle, background: '#f1f5f9', border: '1px solid #cbd5e0', color: '#334155' }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>{t.notFoundTitle}</div>
                {t.notFoundMsg}
                <div>
                  <button
                    type="button"
                    onClick={handleLookupRetry}
                    style={{
                      marginTop: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      fontWeight: 700, fontSize: isMobile ? '0.88rem' : '0.82rem', cursor: 'pointer', border: 'none',
                      padding: isMobile ? '11px 16px' : '7px 13px', borderRadius: 8, color: '#fff', background: '#334155',
                      width: isMobile ? '100%' : undefined,
                    }}
                  >
                    {t.tryAgainBtn}
                  </button>
                </div>
              </div>
            )}

            {step2View.kind === 'incomplete' && (
              <div style={{ ...resultCardStyle, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>{t.incompleteTitle}</div>
                {t.incompleteMsg(step2View.completed, step2View.total)}
                <div>
                  <button
                    type="button"
                    onClick={() => navigate(`/check-in/form/${step2View.formToken}`)}
                    style={{
                      marginTop: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      fontWeight: 700, fontSize: isMobile ? '0.88rem' : '0.82rem', cursor: 'pointer', border: 'none',
                      padding: isMobile ? '11px 16px' : '7px 13px', borderRadius: 8, color: '#fff', background: '#92400e',
                      width: isMobile ? '100%' : undefined,
                    }}
                  >
                    {t.incompleteBtn}
                  </button>
                </div>
              </div>
            )}
          </StepCard>
        </div>

        {contactFooter}
      </div>
    </div>
  );
};

export default PublicCheckinStart;
